import { useState, FC, DragEvent, ChangeEvent } from "react";
import {
    ConversionResult,
    generateSeparateHEIClobs,
    createZipFromBlobs,
} from "../utils/heicToJpgConverter";

const BRAND = "#2563eb";

interface UploadedFile {
    file: File;
    preview: string;
}

interface JPGToPDFConverterProps {
    onBack?: () => void;
}

const HEICToJPGConverter: FC<JPGToPDFConverterProps> = ({ onBack }) => {
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [dragging, setDragging] = useState<boolean>(false);
    const [converting, setConverting] = useState<boolean>(false);
    const [result, setResult] = useState<ConversionResult | null>(null);
    const [zipMultiple, setZipMultiple] = useState<boolean>(false);

    const handleDragOver = (e: DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        setDragging(true);
    };

    const handleDragLeave = (): void => {
        setDragging(false);
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        setDragging(false);
        handleFiles(e.dataTransfer.files);
    };

    const handleFileInput = (e: ChangeEvent<HTMLInputElement>): void => {
        if (e.target.files) {
            handleFiles(e.target.files);
        }
    };

    const handleFiles = async (files: FileList): Promise<void> => {
        const newFiles: UploadedFile[] = [];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];

            // Validate image type
            if (!file.type.startsWith("image/")) {
                setResult({
                    success: false,
                    message: `${file.name} is not a valid image file`,
                });
                continue;
            }

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                newFiles.push({
                    file,
                    preview: e.target?.result as string,
                });

                if (newFiles.length === Array.from(files).filter((f) => f.type.startsWith("image/")).length) {
                    setUploadedFiles((prev) => [...prev, ...newFiles]);
                    setResult(null);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    // rotationMap can be set programmatically or via future per-image controls

    const removeFile = (index: number): void => {
        setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const convertFile = async (file: File): Promise<void> => {
        setConverting(true)
        try {
            const blobs = await generateSeparateHEIClobs([file]);
            if (blobs.length > 0) {
                const { filename, blob } = blobs[0];
                downloadBlob(filename, blob);
                setResult({ success: true, message: `Converted ${file.name} to ${filename}` });
            } else {
                setResult({ success: false, message: "Conversion failed" });
            }
        } catch (err) {
            setResult({ success: false, message: (err as Error).message || "Conversion error" });
        }
        setConverting(false);
    };

    const convertAllFiles = async (): Promise<void> => {
        if (uploadedFiles.length === 0) {
            setResult({
                success: false,
                message: "Please upload at least one image",
            });
            return;
        }

        setConverting(true);
        try {
            const files = uploadedFiles.map((u) => u.file);

                // Separate PDFs
                const pdfs = await generateSeparateHEIClobs(files);
                if (zipMultiple) {
                    const zipBlob = await createZipFromBlobs(pdfs);
                    downloadBlob("images_pdfs.zip", zipBlob);
                    setResult({ success: true, message: `Downloaded ${pdfs.length} JPG in a ZIP` });
                } else {
                    // trigger download for each
                    for (const p of pdfs) {
                        downloadBlob(p.filename, p.blob);
                    }
                    setResult({success: true, message: `Downloaded ${pdfs.length} JPGs`});
                }
            // Clear files after successful conversion
            setUploadedFiles([]);
        } catch (err) {
            setResult({ success: false, message: (err as Error).message || "Batch conversion error" });
        } finally {
            setConverting(false);
        }
    };

    const downloadBlob = (filename: string, blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "40px" }}>
            {/* Back Button */}
            {onBack && (
                <button
                    onClick={onBack}
                    style={{
                        background: "transparent",
                        border: "none",
                        color: "#2563eb",
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: "pointer",
                        marginBottom: 24,
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        transition: "color 0.15s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#1d4ed8")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "#2563eb")}
                >
                    ← Back to Home
                </button>
            )}

            <h1 style={{ fontSize: 36, fontWeight: 700, marginBottom: 12, color: "#1a1a1a" }}>
                HEIC to JPG Converter
            </h1>
            <p style={{ fontSize: 16, color: "#666", marginBottom: 32, lineHeight: 1.6 }}>
                Convert your HEIC images to JPG format instantly. Upload single or multiple images and download as JPG.
            </p>

            {/* Upload Zone */}
            <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => (document.getElementById("jpg-file-input") as HTMLInputElement)?.click()}
                style={{
                    border: `2px dashed ${dragging ? BRAND : "#d1d5db"}`,
                    borderRadius: 16,
                    padding: "56px 24px",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    background: dragging ? "#eff6ff" : "#fafafa",
                    marginBottom: 32,
                }}
            >
                <input
                    id="jpg-file-input"
                    type="file"
                    multiple
                    accept="image/heic"
                    onChange={handleFileInput}
                    style={{ display: "none" }}
                />
                <div style={{ fontSize: 44, marginBottom: 14 }}>🖼️</div>
                <p style={{ fontSize: 17, color: "#444", marginBottom: 8 }}>
                    Drag & drop your images here, or <span style={{ color: BRAND, fontWeight: 600 }}>browse</span>
                </p>
                <p style={{ fontSize: 13, color: "#aaa" }}>
                    Supports HEIC • Max 10 MB per image
                </p>
            </div>

            {/* Uploaded Files Preview */}
            {uploadedFiles.length > 0 && (
                <div style={{ marginBottom: 32 }}>
                    <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16, color: "#1a1a1a" }}>
                        Uploaded Images ({uploadedFiles.length})
                    </h3>
                    <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
                        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <input type="checkbox" checked={zipMultiple} onChange={(e) => setZipMultiple(e.target.checked)}  />
                            Download as ZIP (when separate)
                        </label>
                    </div>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                            gap: 16,
                            marginBottom: 16,
                        }}
                    >
                        {uploadedFiles.map((uploadedFile, index) => (
                            <div
                                key={index}
                                style={{
                                    position: "relative",
                                    borderRadius: 12,
                                    overflow: "hidden",
                                    border: `1px solid #e5e7eb`,
                                }}
                            >
                                <img
                                    src={uploadedFile.preview}
                                    alt={`preview-${index}`}
                                    style={{
                                        width: "100%",
                                        height: 120,
                                        objectFit: "cover",
                                        display: "block",
                                    }}
                                />
                                <button
                                    onClick={() => removeFile(index)}
                                    style={{
                                        position: "absolute",
                                        top: 4,
                                        right: 4,
                                        background: "rgba(0,0,0,0.6)",
                                        border: "none",
                                        color: "#fff",
                                        borderRadius: 4,
                                        width: 28,
                                        height: 28,
                                        cursor: "pointer",
                                        fontSize: 16,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        transition: "background 0.15s",
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0,0,0,0.8)")}
                                    onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(0,0,0,0.6)")}
                                >
                                    ✕
                                </button>

                                {/* Convert individual file button */}
                                <button
                                    onClick={() => convertFile(uploadedFile.file)}
                                    disabled={converting}
                                    style={{
                                        position: "absolute",
                                        bottom: 4,
                                        left: 4,
                                        right: 4,
                                        background: BRAND,
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: 4,
                                        padding: "4px 8px",
                                        fontSize: 11,
                                        fontWeight: 600,
                                        cursor: converting ? "not-allowed" : "pointer",
                                        opacity: converting ? 0.6 : 1,
                                        transition: "background 0.15s",
                                    }}
                                    onMouseEnter={(e) => !converting && (e.currentTarget.style.background = "#1d4ed8")}
                                    onMouseLeave={(e) => !converting && (e.currentTarget.style.background = BRAND)}
                                >
                                    {converting ? "..." : "Convert"}
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* Convert All Button */}
                    <button
                        onClick={convertAllFiles}
                        disabled={converting || uploadedFiles.length === 0}
                        style={{
                            width: "100%",
                            background: BRAND,
                            color: "#fff",
                            border: "none",
                            borderRadius: 10,
                            padding: "14px 28px",
                            fontSize: 16,
                            fontWeight: 600,
                            cursor: converting || uploadedFiles.length === 0 ? "not-allowed" : "pointer",
                            transition: "background 0.15s",
                            opacity: converting || uploadedFiles.length === 0 ? 0.6 : 1,
                        }}
                        onMouseEnter={(e) => !converting && uploadedFiles.length > 0 && (e.currentTarget.style.background = "#1d4ed8")}
                        onMouseLeave={(e) => !converting && uploadedFiles.length > 0 && (e.currentTarget.style.background = BRAND)}
                    >
                        {converting ? "Converting..." : `Convert All (${uploadedFiles.length})`}
                    </button>
                </div>
            )}

            {/* Result Message */}
            {result && (
                <div
                    style={{
                        padding: 16,
                        borderRadius: 12,
                        background: result.success ? "#dcfce7" : "#fee2e2",
                        border: `1px solid ${result.success ? "#bbf7d0" : "#fecaca"}`,
                        color: result.success ? "#165e31" : "#991b1b",
                        fontSize: 14,
                        marginBottom: 24,
                    }}
                >
                    <span style={{ fontWeight: 600 }}>{result.success ? "✓ Success: " : "✗ Error: "}</span>
                    {result.message}
                </div>
            )}

            {/* Info Section */}
            <div
                style={{
                    background: "#f0f4ff",
                    border: `1px solid ${BRAND}`,
                    borderRadius: 12,
                    padding: 20,
                    marginTop: 32,
                }}
            >
                <h3 style={{ fontSize: 14, fontWeight: 600, color: BRAND, marginBottom: 12 }}>ℹ️ How it works</h3>
                <ul style={{ fontSize: 13, color: "#555", lineHeight: 1.8, paddingLeft: 20, margin: 0 }}>
                    <li>Upload HEIC images</li>
                    <li>Each image is converted to a separate JPG file</li>
                    <li>JPG maintain the original image quality and aspect ratio</li>
                    <li>Maximum 10 MB per image</li>
                    <li>No file is stored on our servers - all conversions happen locally</li>
                </ul>
            </div>
        </div>
    );
};

export default HEICToJPGConverter;

